import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { CreditCard, Settings, AlertCircle } from 'lucide-react';

export function BillingSettings() {
  const { user } = useAuth();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="billing-settings">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Billing & Subscriptions</h2>
        <p className="text-sm text-slate-500 mt-1">Manage your plan, payments, and renewal reminders</p>
      </div>

      {/* Current Plan */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-5">
          <CreditCard className="w-4 h-4 text-[#00D1B2]" />
          Current Subscription
        </h3>

        {user?.subscription ? (
          <div className="p-5 bg-[#ECFDF5] rounded-xl border border-[#00D95F]/20">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="text-xl font-bold text-slate-900">
                  {user.subscription.plan_name}
                </h4>
                <p className="text-sm text-slate-500">Active subscription</p>
              </div>
              <span className="px-3 py-1 bg-[#00D95F] text-white text-xs font-semibold rounded-full">
                {user.subscription.status}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Started: {new Date(user.subscription.started_at).toLocaleDateString()}
            </p>
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-7 h-7 text-gray-400" />
            </div>
            <h4 className="text-base font-semibold text-slate-900 mb-1">No Active Subscription</h4>
            <p className="text-sm text-slate-500 mb-4">Choose a plan to unlock all features</p>
            <Button className="bg-[#00D95F] hover:bg-[#00C054] text-white" asChild>
              <Link to="/pricing" data-testid="view-plans-btn">View Plans</Link>
            </Button>
          </div>
        )}
      </Card>

      {/* Payment Status */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-5">
          <AlertCircle className="w-4 h-4 text-amber-500" />
          Payment Status
        </h3>
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-100 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">No overdue payments</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Renewal reminders will appear here when your plan is due
            </p>
          </div>
        </div>
      </Card>

      {/* Payment Methods */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 mb-5">Payment Methods</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 border border-gray-100 rounded-xl text-center bg-gray-50">
            <img
              src="https://razorpay.com/assets/razorpay-logo.svg"
              alt="Razorpay"
              className="h-7 mx-auto mb-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <p className="text-sm font-medium text-slate-700">Razorpay</p>
            <p className="text-xs text-slate-400">Cards, UPI, NetBanking</p>
          </div>
          <div className="p-4 border border-gray-100 rounded-xl text-center bg-gray-50">
            <div className="h-7 flex items-center justify-center mb-2">
              <span className="text-lg font-bold text-blue-600">Unlimit</span>
            </div>
            <p className="text-sm font-medium text-slate-700">Unlimit</p>
            <p className="text-xs text-slate-400">International Cards</p>
          </div>
        </div>
      </Card>

      {/* Billing Actions */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Billing Actions</h3>
        <div className="space-y-2">
          <Button variant="outline" className="w-full justify-start gap-2" asChild>
            <Link to="/pricing">
              <CreditCard className="w-4 h-4" />
              {user?.subscription ? 'Upgrade Plan' : 'Choose a Plan'}
            </Link>
          </Button>
          <Button variant="outline" className="w-full justify-start gap-2 text-slate-400" disabled>
            <Settings className="w-4 h-4" />
            Payment History
            <span className="ml-auto text-xs">Coming soon</span>
          </Button>
          <Button variant="outline" className="w-full justify-start gap-2 text-slate-400" disabled>
            <Settings className="w-4 h-4" />
            Invoices
            <span className="ml-auto text-xs">Coming soon</span>
          </Button>
        </div>
        <div className="mt-5 pt-5 border-t border-gray-100">
          <Button variant="outline" className="w-full" asChild>
            <Link to="/contact">Contact Support</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}
